import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFlash } from "../../context/flashcontext";
import RegisterComponent from "./RegisterComponent";
import { registerUser, fetchInviteInfo } from "../../utils/authApiHelpers";
import { useKvKLookup } from "../../hooks/useKvKLookup";

function RegisterContainer() {
    const { code } = useParams();
    const { showFlash } = useFlash();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        phone: "",
        address: "",
        kvk_number: "",
        company_name: "",
        role: "zzper",
        vat_number: ""
    });
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [vatError, setVatError] = useState("");
    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [checkboxError, setCheckboxError] = useState("");
    const { kvkError, loadingKvk, fetchKvKData } = useKvKLookup(setFormData);

    const validateDutchVat = (vat) => /^NL\d{9}B\d{2}$/.test(vat);

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmPassword(value);

        // Check if passwords match
        if (value && formData.password && value !== formData.password) {
            setPasswordError("Wachtwoorden komen niet overeen");
        } else {
            setPasswordError("");
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Check if passwords match when main password changes
        if (confirmPassword && value !== confirmPassword) {
            setPasswordError("Wachtwoorden komen niet overeen");
        } else {
            setPasswordError("");
        }
    };

    const handleVatChange = (e) => {
        handleChange(e);
        const value = e.target.value;
        if (value && !validateDutchVat(value)) {
            setVatError("Ongeldig Nederlands BTW-nummer (bijv. NL123456789B01)");
        } else {
            setVatError("");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Use special handler for password field
        if (name === 'password') {
            handlePasswordChange(e);
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCheckboxChange = (e) => {
        setCheckboxChecked(e.target.checked);
        if (e.target.checked) setCheckboxError("");
    };

    const fetchRoleData = async () => {
        if (code !== undefined) {
            const response = await fetchInviteInfo(code);
            if (response.data.role !== "") {
                setFormData(prev => ({ ...prev, role: response.data.role, email: response.data.email, userId: response.data.id }));
            }
        }
    };

    useEffect(() => {
        fetchRoleData();
    }, [fetchRoleData]);

    // Automate KvK lookup when more than 3 characters are filled in kvk_number
    useEffect(() => {
        if (formData.kvk_number && formData.kvk_number.length > 3) {
            fetchKvKData(formData.kvk_number);
        }
        // Only trigger when kvk_number changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.kvk_number]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!checkboxChecked) {
            setCheckboxError("Je moet akkoord gaan met de algemene voorwaarden.");
            return;
        }

        // Check if passwords match
        if (formData.password !== confirmPassword) {
            setPasswordError("Wachtwoorden komen niet overeen");
            showFlash("Wachtwoorden komen niet overeen", "error");
            return;
        }

        if (kvkError || vatError) {
            showFlash("Corrigeer eerst de KvK- of BTW-nummer fouten.", "error");
            return;
        }
        for (const field in formData) {
            if (!formData[field]) {
                showFlash(`Veld ${field} mag niet leeg zijn.`, "error");
                return;
            }
        }
        try {
            await registerUser(formData);
            showFlash("Registratie succesvol!", "success");
            navigate("/login", { replace: true });
        } catch (err) {
            console.error("Registration error:", err);
            if (err.response?.status === 409) {
                showFlash("E-mailadres is al in gebruik.", "error");
            } else {
                showFlash("Registratie mislukt. Probeer opnieuw.", "error");
            }
        }
    };

    return (
        <RegisterComponent
            formData={formData}
            confirmPassword={confirmPassword}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            passwordError={passwordError}
            vatError={vatError}
            kvkError={kvkError}
            loadingKvk={loadingKvk}
            onChange={handleChange}
            onVatChange={handleVatChange}
            onKvKLookup={() => fetchKvKData(formData.kvk_number)}
            onSubmit={handleSubmit}
            checkboxChecked={checkboxChecked}
            onCheckboxChange={handleCheckboxChange}
            checkboxError={checkboxError}
            setCheckboxError={setCheckboxError}
        />
    );
}

export default RegisterContainer;

RegisterContainer.propTypes = {
    // No props - uses useParams
};
